import { useState } from "react";
import type { BoardRecoveryCandidate } from "../persistence/board_session";
import { Modal } from "./modal";

export interface RecoveryCopyDialogProps {
  readonly candidate: BoardRecoveryCandidate;
  readonly canonicalUpdatedAt: string;
  readonly onDiscard: () => Promise<void>;
  readonly onDownload: () => void;
  readonly onRestore: () => Promise<void>;
  readonly onSaveCopy: () => Promise<void>;
}

/** Compares browser-local recovery with canonical disk state before editing resumes. */
export function RecoveryCopyDialog({
  candidate,
  canonicalUpdatedAt,
  onDiscard,
  onDownload,
  onRestore,
  onSaveCopy,
}: RecoveryCopyDialogProps) {
  const [busyAction, setBusyAction] = useState<"discard" | "restore" | "copy" | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Runs one recovery decision and keeps the candidate available after failure. */
  const run = async (
    action: "discard" | "restore" | "copy",
    operation: () => Promise<void>,
  ): Promise<void> => {
    setBusyAction(action);
    setError(null);
    try {
      await operation();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The recovery copy remains available.");
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <Modal
      title="Newer recovery copy found"
      titleId="recovery-copy-title"
      descriptionId="recovery-copy-description"
      onClose={() => undefined}
    >
      <div className="decision-dialog">
        <p id="recovery-copy-description" className="modal__lede">
          Shotboard found newer browser-local work. Restoring it is recommended. The disk board
          stays unchanged until you choose.
        </p>
        <dl className="decision-details">
          <div>
            <dt>Disk version</dt>
            <dd>{formatTimestamp(canonicalUpdatedAt)}</dd>
          </div>
          <div>
            <dt>Recovery version</dt>
            <dd>{formatTimestamp(candidate.record.savedAt)}</dd>
          </div>
        </dl>
        {error === null ? null : (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="decision-secondary-actions">
          <button type="button" disabled={busyAction !== null} onClick={onDownload}>
            Download Recovery JSON
          </button>
          <button
            type="button"
            disabled={busyAction !== null}
            onClick={() => void run("copy", onSaveCopy)}
          >
            {busyAction === "copy" ? "Saving copy…" : "Save Recovery as Copy"}
          </button>
        </div>
        <footer className="modal__actions">
          <button
            type="button"
            className="secondary-action"
            disabled={busyAction !== null}
            onClick={() => void run("discard", onDiscard)}
          >
            {busyAction === "discard" ? "Opening…" : "Open Disk Version"}
          </button>
          <button
            type="button"
            className="primary-action"
            disabled={busyAction !== null}
            onClick={() => void run("restore", onRestore)}
          >
            {busyAction === "restore" ? "Restoring…" : "Restore Recovery"}
          </button>
        </footer>
      </div>
    </Modal>
  );
}

/** Formats persisted ISO timestamps in the user's local timezone. */
function formatTimestamp(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}
